import { classes } from "./classes.js";
import {
  BANK_BG_SURFACE,
  BANK_BORDER_STRONG,
  BANK_FOCUS,
  BANK_RADIUS_CTL,
  BANK_FS_LEAD,
  BANK_TEXT,
  BANK_TEXT_BRAND,
} from "./tokens.js";

/**
 * `F-0b`: `−` figure `+` (prototype `.stepper`), for a policy's rates and threshold.
 *
 * At a bound the button is **`aria-disabled`, never `disabled`**, for the reason `GuardedButton` gives: a
 * `disabled` button leaves the tab order and a person never learns why it is dead. It does nothing when pressed.
 * Steps are cut to 12 significant digits (a rate is not money; no FCFA passes here) so `0,1 + 0,1` is `0,2` and not `0,2000000004`.
 *
 * The buttons' names and the group's are the caller's (catalogue); the figure's text is `format`'s.
 */
export interface NumberStepperProps {
  readonly label: string;
  readonly decreaseLabel: string;
  readonly increaseLabel: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  /** How the figure reads: units and decimal comma are the caller's. */
  readonly format?: (value: number) => string;
  readonly className?: string;
  readonly testId?: string;
}

const BUTTON = classes(
  "flex h-[42px] w-[42px] items-center justify-center text-[length:20px] font-semibold",
  BANK_BG_SURFACE,
  BANK_TEXT_BRAND,
  "hover:bg-[color:var(--hk-brand-wash)]",
  BANK_FOCUS,
);

export function NumberStepper({
  label,
  decreaseLabel,
  increaseLabel,
  value,
  onChange,
  min,
  max,
  step = 1,
  format = String,
  className,
  testId,
}: NumberStepperProps): React.JSX.Element {
  const next = (direction: 1 | -1): number => Number.parseFloat((value + direction * step).toPrecision(12));
  const atMin = min !== undefined && next(-1) < min;
  const atMax = max !== undefined && next(1) > max;
  return (
    <div
      role="group"
      aria-label={label}
      data-kit="stepper"
      data-testid={testId}
      className={classes(
        "inline-flex items-stretch overflow-hidden border",
        BANK_BORDER_STRONG,
        BANK_RADIUS_CTL,
        className,
      )}
    >
      <button
        type="button"
        aria-label={decreaseLabel}
        aria-disabled={atMin ? "true" : undefined}
        onClick={() => {
          if (!atMin) onChange(next(-1));
        }}
        className={BUTTON}
      >
        <span aria-hidden="true">−</span>
      </button>
      <output
        aria-live="polite"
        className={classes(
          "flex min-w-[92px] items-center justify-center font-[family-name:var(--hk-mono)] font-semibold [font-variant-numeric:tabular-nums]",
          BANK_FS_LEAD,
          BANK_TEXT,
        )}
      >
        {format(value)}
      </output>
      <button
        type="button"
        aria-label={increaseLabel}
        aria-disabled={atMax ? "true" : undefined}
        onClick={() => {
          if (!atMax) onChange(next(1));
        }}
        className={BUTTON}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
